import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';

const formatMonthLabel = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' });
};

const Sparkline = ({ data, height = 48, color = '#0ea5b7' }) => {
  const values = (data || []).map((d) => Number(d.count || 0));
  if (values.length === 0) {
    return <div className="text-muted small">No data</div>;
  }
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const width = 160;
  const points = values.map((value, index) => {
    const x = (index / (values.length - 1 || 1)) * (width - 4) + 2;
    const y = height - 4 - ((value - min) / range) * (height - 8);
    return `${x},${y}`;
  }).join(' ');
  const firstLabel = formatMonthLabel(data?.[0]?.month);
  const midLabel = formatMonthLabel(data?.[Math.floor((data?.length || 1) / 2)]?.month);
  const lastLabel = formatMonthLabel(data?.[(data?.length || 1) - 1]?.month);
  return (
    <div className="border rounded-3 bg-white p-2">
      <div className="text-end">
        <svg width={width} height={height} role="img" aria-label="trend">
          <polyline
            fill="none"
            stroke={color}
            strokeWidth="2"
            points={points}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <div className="d-flex justify-content-between text-muted small mt-1">
          <span>{firstLabel}</span>
          <span>{midLabel}</span>
          <span>{lastLabel}</span>
        </div>
      </div>
    </div>
  );
};
const GroupedBarChart = ({ seriesA = [], seriesB = [], height = 220, colorA = '#0f766e', colorB = '#b45309', labelA = 'New Paid', labelB = 'Canceled' }) => {
  const chartData = (seriesA || []).map((item, index) => ({
    month: formatMonthLabel(item.month),
    a: Number(item.count || 0),
    b: Number(seriesB?.[index]?.count || 0),
  }));
  if (chartData.length === 0) {
    return <div className="text-muted small">No data</div>;
  }
  const width = 760;
  const leftPad = 44;
  const rightPad = 16;
  const topPad = 18;
  const bottomPad = 34;
  const chartWidth = width - leftPad - rightPad;
  const chartHeight = height - topPad - bottomPad;
  const maxValue = Math.max(...chartData.flatMap((item) => [item.a, item.b]), 1);
  const yMax = maxValue <= 5 ? 5 : Math.ceil(maxValue / 5) * 5;
  const yTicks = 5;
  const groupWidth = chartWidth / chartData.length;
  const innerGroupWidth = Math.max(18, groupWidth - 10);
  const barGap = 4;
  const barWidth = Math.max(6, Math.min(20, (innerGroupWidth - barGap) / 2));

  return (
    <div className="border rounded-3 bg-white p-2">
      <div className="d-flex gap-3 small text-muted mb-2">
        <span><span className="me-1" style={{ display: 'inline-block', width: 10, height: 10, backgroundColor: colorA, borderRadius: 2 }} />{labelA}</span>
        <span><span className="me-1" style={{ display: 'inline-block', width: 10, height: 10, backgroundColor: colorB, borderRadius: 2 }} />{labelB}</span>
      </div>
      <div style={{ width: '100%', overflowX: 'auto' }}>
        <svg
          width="100%"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ display: 'block', minWidth: '640px', height: `${height}px` }}
          role="img"
          aria-label="subscription trend chart"
        >
          {Array.from({ length: yTicks + 1 }).map((_, index) => {
            const value = (yMax / yTicks) * index;
            const y = topPad + chartHeight - ((value / yMax) * chartHeight);
            return (
              <g key={`grid-${value}`}>
                <line x1={leftPad} y1={y} x2={width - rightPad} y2={y} stroke="#e5e7eb" strokeWidth="1" />
                <text x={leftPad - 8} y={y + 4} textAnchor="end" fontSize="10" fill="#6b7280">
                  {Math.round(value)}
                </text>
              </g>
            );
          })}
          <line x1={leftPad} y1={topPad} x2={leftPad} y2={topPad + chartHeight} stroke="#94a3b8" strokeWidth="1" />
          <line x1={leftPad} y1={topPad + chartHeight} x2={width - rightPad} y2={topPad + chartHeight} stroke="#94a3b8" strokeWidth="1" />
          {chartData.map((item, index) => {
            const x = leftPad + (index * groupWidth) + (groupWidth - innerGroupWidth) / 2;
            const aHeight = (item.a / yMax) * chartHeight;
            const bHeight = (item.b / yMax) * chartHeight;
            return (
              <g key={`group-${item.month}-${index}`}>
                <rect
                  x={x}
                  y={topPad + chartHeight - aHeight}
                  width={barWidth}
                  height={aHeight}
                  fill={colorA}
                  rx="2"
                />
                <rect
                  x={x + barWidth + barGap}
                  y={topPad + chartHeight - bHeight}
                  width={barWidth}
                  height={bHeight}
                  fill={colorB}
                  rx="2"
                />
                <text
                  x={x + innerGroupWidth / 2}
                  y={height - 10}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#6b7280"
                >
                  {item.month}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
};
const BarChart = ({ data = [], height = 240, color = '#0f766e' }) => {
  if (!data.length) {
    return <div className="text-muted small">No data</div>;
  }
  const width = 760;
  const leftPad = 52;
  const rightPad = 16;
  const topPad = 18;
  const bottomPad = 34;
  const chartWidth = width - leftPad - rightPad;
  const chartHeight = height - topPad - bottomPad;
  const maxValue = Math.max(...data.map((item) => Number(item.request_count || 0)), 1);
  const yMax = maxValue <= 10 ? 10 : Math.ceil(maxValue / 10) * 10;
  const yTicks = 5;
  const groupWidth = chartWidth / data.length;
  const barWidth = Math.max(14, Math.min(36, groupWidth - 10));

  return (
    <div className="border rounded-3 bg-white p-2">
      <div style={{ width: '100%', overflowX: 'auto' }}>
        <svg
          width="100%"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ display: 'block', minWidth: '640px', height: `${height}px` }}
          role="img"
          aria-label="service api request history chart"
        >
          {Array.from({ length: yTicks + 1 }).map((_, index) => {
            const value = (yMax / yTicks) * index;
            const y = topPad + chartHeight - ((value / yMax) * chartHeight);
            return (
              <g key={`bar-grid-${value}`}>
                <line x1={leftPad} y1={y} x2={width - rightPad} y2={y} stroke="#e5e7eb" strokeWidth="1" />
                <text x={leftPad - 8} y={y + 4} textAnchor="end" fontSize="10" fill="#6b7280">
                  {formatRequestCount(Math.round(value))}
                </text>
              </g>
            );
          })}
          <line x1={leftPad} y1={topPad} x2={leftPad} y2={topPad + chartHeight} stroke="#94a3b8" strokeWidth="1" />
          <line x1={leftPad} y1={topPad + chartHeight} x2={width - rightPad} y2={topPad + chartHeight} stroke="#94a3b8" strokeWidth="1" />
          {data.map((item, index) => {
            const barHeight = (Number(item.request_count || 0) / yMax) * chartHeight;
            const x = leftPad + (index * groupWidth) + ((groupWidth - barWidth) / 2);
            return (
              <g key={`bar-${item.month}-${index}`}>
                <rect
                  x={x}
                  y={topPad + chartHeight - barHeight}
                  width={barWidth}
                  height={barHeight}
                  fill={color}
                  rx="2"
                />
                <text
                  x={x + (barWidth / 2)}
                  y={height - 10}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#6b7280"
                >
                  {formatMonthLabel(item.month)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
};

const formatCurrency = (cents) => `$${((Number(cents) || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const formatSignedCurrency = (cents) => {
  const value = Number(cents) || 0;
  if (value === 0) return '$0';
  return `${value > 0 ? '+' : '-'}${formatCurrency(Math.abs(value))}`;
};
const formatPercent = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const numeric = Number(value);
  if (numeric === 0) return '0%';
  return `${numeric > 0 ? '+' : ''}${numeric}%`;
};
const formatPlainPercent = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return `${Number(value)}%`;
};
const formatErrorPercent = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const numeric = Number(value);
  if (numeric > 0 && numeric < 0.1) return '<0.1%';
  return `${numeric.toFixed(numeric < 1 ? 2 : 1)}%`;
};
const formatRequestCount = (value) => Number(value || 0).toLocaleString('en-US');
const formatDurationMs = (value) => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return '—';
  if (numeric >= 1000) return `${(numeric / 1000).toFixed(2)}s`;
  return `${numeric.toFixed(1)} ms`;
};
const formatCadenceLabel = (value) => {
  if (value === 'monthly') return 'Monthly';
  if (value === 'annual') return 'Annual';
  return value || '—';
};
const formatSubscriptionType = (name) => name || 'None';
const formatDateCell = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('en-US', { timeZone: 'UTC' });
};
const SectionCard = ({ title, children, first = false }) => (
  <div className={first ? 'rounded-3 border bg-light-subtle p-3 mb-3' : 'rounded-3 border bg-light-subtle p-3 mb-3 mt-4'}>
    <h6 className="fw-semibold mb-3 text-dark">{title}</h6>
    {children}
  </div>
);
const FourColumnTableColGroup = () => (
  <colgroup>
    <col style={{ width: '34%' }} />
    <col style={{ width: '18%' }} />
    <col style={{ width: '28%' }} />
    <col style={{ width: '20%' }} />
  </colgroup>
);
const FourColumnUtilTableColGroup = () => (
  <colgroup>
    <col style={{ width: '34%' }} />
    <col style={{ width: '18%' }} />
    <col style={{ width: '28%' }} />
    <col style={{ width: '20%' }} />
  </colgroup>
);
const FiveColumnTableColGroup = () => (
  <colgroup>
    <col style={{ width: '28%' }} />
    <col style={{ width: '16%' }} />
    <col style={{ width: '24%' }} />
    <col style={{ width: '16%' }} />
    <col style={{ width: '16%' }} />
  </colgroup>
);
const SixColumnTableColGroup = () => (
  <colgroup>
    <col style={{ width: '22%' }} />
    <col style={{ width: '15%' }} />
    <col style={{ width: '21%' }} />
    <col style={{ width: '14%' }} />
    <col style={{ width: '14%' }} />
    <col style={{ width: '14%' }} />
  </colgroup>
);

const OpsUserMetrics = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('subscriptions');

  const inviteAcceptanceRate = useMemo(() => {
    if (!metrics) return 0;
    const total = metrics.invites_last_30 || 0;
    const accepted = metrics.invites_accepted_last_30 || 0;
    if (total === 0) return 0;
    return Math.round((accepted / total) * 100);
  }, [metrics]);

  const subscriptionRevenue = metrics?.subscription_revenue || {};
  const actualSubscriptionRevenue = metrics?.actual_subscription_revenue || {};
  const subscriptionTrends = metrics?.subscription_trends || {};
  const serviceApiMetrics = metrics?.service_api_metrics || {};
  const serviceApiRouteMetrics = metrics?.service_api_route_metrics || {};
  const tierEntries = useMemo(() => {
    const entries = Object.entries(subscriptionRevenue.subscribers_by_tier || {});
    const order = { enterprise: 0, growth: 1, starter: 2, trial: 3, none: 4 };
    return entries.sort((a, b) => (order[a[0]] ?? 99) - (order[b[0]] ?? 99));
  }, [subscriptionRevenue.subscribers_by_tier]);
  const actualRevenueByTier = useMemo(() => actualSubscriptionRevenue.revenue_by_tier || [], [actualSubscriptionRevenue.revenue_by_tier]);
  const topLifetimeSpendOrgs = useMemo(
    () => actualSubscriptionRevenue.top_organizations_by_lifetime_spend || [],
    [actualSubscriptionRevenue.top_organizations_by_lifetime_spend]
  );
  const activeByTier = useMemo(() => {
    const entries = Object.entries(subscriptionTrends.active_by_tier || {});
    const order = { enterprise: 0, growth: 1, starter: 2, trial: 3, none: 4 };
    return entries.sort((a, b) => (order[a[0]] ?? 99) - (order[b[0]] ?? 99));
  }, [subscriptionTrends.active_by_tier]);
  const activeByCadence = useMemo(() => Object.entries(subscriptionTrends.active_by_cadence || {}), [subscriptionTrends.active_by_cadence]);
  const scheduledCancellationByTier = useMemo(() => {
    const entries = Object.entries(subscriptionTrends.scheduled_cancellations_by_tier || {});
    const order = { enterprise: 0, growth: 1, starter: 2, trial: 3, none: 4 };
    return entries.sort((a, b) => (order[a[0]] ?? 99) - (order[b[0]] ?? 99));
  }, [subscriptionTrends.scheduled_cancellations_by_tier]);

  const loadMetrics = async (mounted = { current: true }) => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/acct-mgmt/v1/metrics/summary');
      if (!mounted.current) return;
      setMetrics(response.data);
    } catch (err) {
      if (!mounted.current) return;
      setError(err.response?.data?.error || 'Failed to load metrics');
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  useEffect(() => {
    const mounted = { current: true };
    loadMetrics(mounted);
    return () => {
      mounted.current = false;
    };
  }, []);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h2 className="h4 fw-bold mb-1">Dashboard</h2>
          <p className="text-muted mb-0">Key platform metrics and support health at a glance.</p>
        </div>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={() => loadMetrics()}
          disabled={loading}
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>
      <div className="d-flex flex-wrap gap-2 mb-3">
        <button
          type="button"
          className={`btn btn-sm ${activeTab === 'subscriptions' ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => setActiveTab('subscriptions')}
        >
          Subscriptions & Revenue
        </button>
        <button
          type="button"
          className={`btn btn-sm ${activeTab === 'service-api-utilization' ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => setActiveTab('service-api-utilization')}
        >
          Service API Utilization Metrics
        </button>
        <button
          type="button"
          className={`btn btn-sm ${activeTab === 'service-api-endpoint-analytics' ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => setActiveTab('service-api-endpoint-analytics')}
        >
          Service API Endpoint Analytics
        </button>
      </div>
      {loading && <div className="text-muted">Loading metrics...</div>}
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {!loading && metrics && activeTab === 'subscriptions' && (
        <>
          <div className="row g-3 mb-4">
            <div className="col-lg-3 col-md-6">
              <div className="card h-100">
                <div className="card-body">
                  <div className="text-muted small">Total Organizations</div>
                  <div className="fw-semibold fs-4">
                    {(metrics.org_counts?.['type-org'] || 0) + (metrics.org_counts?.['type-ind'] || 0)}
                  </div>
                  <div className="text-muted small mt-2">
                    Org: {metrics.org_counts?.['type-org'] || 0} · Individual: {metrics.org_counts?.['type-ind'] || 0}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card h-100">
                <div className="card-body">
                  <div className="text-muted small">Total Users</div>
                  <div className="fw-semibold fs-4">{metrics.user_count || 0}</div>
                  <div className="text-muted small mt-2">New (30 days): {metrics.users_last_30 || 0}</div>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card h-100">
                <div className="card-body">
                  <div className="text-muted small">New Orgs (30 days)</div>
                  <div className="fw-semibold fs-4">{metrics.orgs_last_30 || 0}</div>
                  <div className="text-muted small mt-2">New Users (30 days): {metrics.users_last_30 || 0}</div>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card h-100">
                <div className="card-body">
                  <div className="text-muted small">Invite Acceptance (30 days)</div>
                  <div className="fw-semibold fs-4">{inviteAcceptanceRate}%</div>
                  <div className="text-muted small mt-2">
                    {metrics.invites_accepted_last_30 || 0} accepted · {metrics.invites_last_30 || 0} sent
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-3 mb-4">
            <div className="col-lg-6">
              <div className="card h-100">
                <div className="card-body">
                  <h5 className="card-title">Organization Growth (12 months)</h5>
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="text-muted small">Monthly org registrations</div>
                    <Sparkline data={metrics.org_trend} />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="card h-100">
                <div className="card-body">
                  <h5 className="card-title">User Growth (12 months)</h5>
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="text-muted small">Monthly user registrations</div>
                    <Sparkline data={metrics.user_trend} color="#2563eb" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h5 className="card-title">Projected Subscription Revenue</h5>
              <div className="text-muted small mb-3">Derived from local subscription plan/version state. These are projected recurring revenue metrics, not Stripe-collected actuals.</div>
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <div className="text-muted small">Current Month Revenue (Projected)</div>
                  <div className="fw-semibold fs-5">{formatCurrency(subscriptionRevenue.current_month_projected_revenue_cents)}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">Current Paid Subscribers</div>
                  <div className="fw-semibold fs-5">{subscriptionRevenue.current_paid_subscribers || 0}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">Trialing Orgs</div>
                  <div className="fw-semibold fs-5">{subscriptionRevenue.trialing_subscribers || 0}</div>
                </div>
              </div>
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <div className="text-muted small">Next Month Revenue (Projected)</div>
                  <div className="fw-semibold">{formatCurrency(subscriptionRevenue.next_month_projected_revenue_cents)}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">Change from Current Month</div>
                  <div className="fw-semibold">{formatSignedCurrency(subscriptionRevenue.next_month_change_cents)}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">MoM Growth</div>
                  <div className="fw-semibold">{formatPercent(subscriptionRevenue.mom_growth_pct)}</div>
                </div>
              </div>
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <div className="text-muted small">YTD Revenue</div>
                  <div className="fw-semibold">{formatCurrency(subscriptionRevenue.ytd_revenue_cents)}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">Projected Annual Revenue</div>
                  <div className="fw-semibold">{formatCurrency(subscriptionRevenue.projected_annual_revenue_cents)}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">Annual Run Rate</div>
                  <div className="fw-semibold">{formatCurrency(subscriptionRevenue.annual_run_rate_cents)}</div>
                </div>
              </div>
              <div className="row g-3">
                <div className="col-md-8">
                  <div className="text-muted small mb-1">Subscribers by Tier</div>
                  {tierEntries.length === 0 ? (
                    <div className="text-muted">No subscription data</div>
                  ) : (
                    <div className="d-flex flex-wrap gap-2">
                      {tierEntries.map(([planCode, item]) => (
                        <span key={planCode} className="badge bg-light text-dark border">
                          {item.plan_name || planCode}: {item.count}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h5 className="card-title">Actual Subscription Revenue</h5>
              <div className="text-muted small mb-3">Stripe-backed billing, invoice, and payment metrics based on synced invoice history.</div>
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <div className="text-muted small">Current Month Revenue</div>
                  <div className="fw-semibold fs-5">{formatCurrency(actualSubscriptionRevenue.current_month_revenue_cents)}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">Prior Month Revenue</div>
                  <div className="fw-semibold fs-5">{formatCurrency(actualSubscriptionRevenue.prior_month_revenue_cents)}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">MoM Growth</div>
                  <div className="fw-semibold fs-5">{formatPercent(actualSubscriptionRevenue.mom_growth_pct)}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">Paid Invoices This Month</div>
                  <div className="fw-semibold">{actualSubscriptionRevenue.paid_invoices_this_month || 0}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">Failed Payments This Month</div>
                  <div className="fw-semibold">{actualSubscriptionRevenue.failed_payments_this_month || 0}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">Refunds / Credits This Month</div>
                  <div className="fw-semibold">{formatCurrency(actualSubscriptionRevenue.refunds_credits_this_month_cents)}</div>
                </div>
              </div>
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <div className="text-muted small">Current Year Revenue to Date</div>
                  <div className="fw-semibold">{formatCurrency(actualSubscriptionRevenue.current_year_revenue_cents)}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">Prior Year Revenue to Date</div>
                  <div className="fw-semibold">{formatCurrency(actualSubscriptionRevenue.prior_year_ytd_revenue_cents)}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">YoY Growth</div>
                  <div className="fw-semibold">{formatPercent(actualSubscriptionRevenue.yoy_growth_pct)}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">MRR</div>
                  <div className="fw-semibold">{formatCurrency(actualSubscriptionRevenue.mrr_cents)}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">ARR</div>
                  <div className="fw-semibold">{formatCurrency(actualSubscriptionRevenue.arr_cents)}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">Tax Collected This Month</div>
                  <div className="fw-semibold">{formatCurrency(actualSubscriptionRevenue.tax_collected_this_month_cents)}</div>
                </div>
              </div>
              <div className="row g-3">
                <div className="col-md-4">
                  <div className="text-muted small">Active Paid Subscribers</div>
                  <div className="fw-semibold">{actualSubscriptionRevenue.active_paid_subscribers || 0}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">New Paid Subscribers This Month</div>
                  <div className="fw-semibold">{actualSubscriptionRevenue.new_paid_subscribers_this_month || 0}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">Churned Subscribers This Month</div>
                  <div className="fw-semibold">{actualSubscriptionRevenue.churned_subscribers_this_month || 0}</div>
                </div>
                <div className="col-12">
                  <div className="text-muted small mb-1">Revenue by Tier (Current Month)</div>
                  {actualRevenueByTier.length === 0 ? (
                    <div className="text-muted">No paid invoice activity this month</div>
                  ) : (
                    <div className="d-flex flex-wrap gap-2">
                      {actualRevenueByTier.map((item) => (
                        <span key={item.plan_code} className="badge bg-light text-dark border">
                          {item.plan_name}: {formatCurrency(item.revenue_cents)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="col-12">
                  <div className="text-muted small mb-1">Top 5 Organizations by Lifetime Spend</div>
                  {topLifetimeSpendOrgs.length === 0 ? (
                    <div className="text-muted">No paid billing history recorded yet</div>
                  ) : (
                    <div className="d-flex flex-wrap gap-2">
                      {topLifetimeSpendOrgs.map((item) => (
                        <span key={item.org_id} className="badge bg-light text-dark border">
                          {item.org_name}: {formatCurrency(item.lifetime_spend_cents)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h5 className="card-title">Subscription Trends</h5>
              <div className="text-muted small mb-3">Subscriber movement, active mix, and forward-looking churn risk.</div>
              <div className="row g-3 mb-3">
                <div className="col-md-3">
                  <div className="text-muted small">Active Paid Subscriptions</div>
                  <div className="fw-semibold">{subscriptionTrends.active_paid_subscriptions || 0}</div>
                </div>
                <div className="col-md-3">
                  <div className="text-muted small">New Paid Subscriptions This Month</div>
                  <div className="fw-semibold">{subscriptionTrends.new_paid_subscriptions_this_month || 0}</div>
                </div>
                <div className="col-md-3">
                  <div className="text-muted small">Scheduled Cancellations</div>
                  <div className="fw-semibold">{subscriptionTrends.scheduled_cancellations || 0}</div>
                </div>
                <div className="col-md-3">
                  <div className="text-muted small">Net Subscription Change This Month</div>
                  <div className="fw-semibold">{subscriptionTrends.net_subscription_change_this_month || 0}</div>
                </div>
              </div>
              <div className="row g-3 mb-3">
                <div className="col-12">
                  <div className="text-muted small mb-1">New Paid vs Canceled Subscriptions (12 months)</div>
                  <GroupedBarChart
                    seriesA={subscriptionTrends.new_subscriptions_trend}
                    seriesB={subscriptionTrends.canceled_subscriptions_trend}
                    colorA="#0f766e"
                    colorB="#b45309"
                    labelA="New Paid"
                    labelB="Canceled"
                  />
                </div>
              </div>
              <div className="row g-3">
                <div className="col-lg-4">
                  <div className="text-muted small mb-1">Active by Tier</div>
                  {activeByTier.length === 0 ? (
                    <div className="text-muted">No active subscriptions</div>
                  ) : (
                    <div className="d-flex flex-wrap gap-2">
                      {activeByTier.map(([planCode, item]) => (
                        <span key={planCode} className="badge bg-light text-dark border">
                          {item.plan_name}: {item.count}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="col-lg-4">
                  <div className="text-muted small mb-1">Active by Billing Cadence</div>
                  {activeByCadence.length === 0 ? (
                    <div className="text-muted">No active subscriptions</div>
                  ) : (
                    <div className="d-flex flex-wrap gap-2">
                      {activeByCadence.map(([cadence, count]) => (
                        <span key={cadence} className="badge bg-light text-dark border">
                          {formatCadenceLabel(cadence)}: {count}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="col-lg-4">
                  <div className="text-muted small mb-1">Scheduled Cancellations by Tier</div>
                  {scheduledCancellationByTier.length === 0 ? (
                    <div className="text-muted">No scheduled cancellations</div>
                  ) : (
                    <div className="d-flex flex-wrap gap-2">
                      {scheduledCancellationByTier.map(([planCode, item]) => (
                        <span key={planCode} className="badge bg-light text-dark border">
                          {item.plan_name}: {item.count}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="col-12">
                  <div className="text-muted small mb-1">Upcoming Scheduled Cancellations</div>
                  {(subscriptionTrends.upcoming_scheduled_cancellations || []).length === 0 ? (
                    <div className="text-muted">No scheduled cancellations</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Tier</th>
                            <th>Cadence</th>
                            <th>Effective Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(subscriptionTrends.upcoming_scheduled_cancellations || []).map((item, index) => (
                            <tr key={`${item.plan_code}-${item.effective_on}-${index}`}>
                              <td>{item.plan_name}</td>
                              <td>{formatCadenceLabel(item.billing_cadence)}</td>
                              <td>{formatDateCell(item.effective_on)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h5 className="card-title">Registration Activity</h5>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Time Window</th>
                      <th>Organizations</th>
                      <th>Users</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="fw-semibold">Last 30 Days</td>
                      <td>{metrics.orgs_last_30 || 0}</td>
                      <td>{metrics.users_last_30 || 0}</td>
                    </tr>
                    <tr>
                      <td className="fw-semibold">Last 60 Days</td>
                      <td>{metrics.orgs_last_60 || 0}</td>
                      <td>{metrics.users_last_60 || 0}</td>
                    </tr>
                    <tr>
                      <td className="fw-semibold">Last 90 Days</td>
                      <td>{metrics.orgs_last_90 || 0}</td>
                      <td>{metrics.users_last_90 || 0}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h5 className="card-title">Support Health</h5>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Time Window</th>
                      <th>Failed Logins</th>
                      <th>Auth Throttles Triggered</th>
                      <th>Support Actions Logged</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="fw-semibold">Last 7 Days</td>
                      <td>{metrics.failed_logins_last_7 || 0}</td>
                      <td>{metrics.auth_throttles_last_7 || 0}</td>
                      <td>{metrics.support_actions_last_7 || 0}</td>
                    </tr>
                    <tr>
                      <td className="fw-semibold">Last 30 Days</td>
                      <td>{metrics.failed_logins_last_30 || 0}</td>
                      <td>{metrics.auth_throttles_last_30 || 0}</td>
                      <td>{metrics.support_actions_last_30 || 0}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
      {!loading && metrics && activeTab === 'service-api-utilization' && (
        <>
          <div className="card mb-4">
            <div className="card-body">
              <h5 className="card-title">API Utilization Trends</h5>
              <div className="text-muted small mb-3">
                Request volume trends based on monthly Service API utilization totals. Service API metrics are near real time and may lag slightly behind live request activity.
              </div>
              <SectionCard title="Request Volume Summary" first>
                <div className="row g-3">
                  <div className="col-md-4">
                    <div className="text-muted small">API Requests Fulfilled (Previous Month)</div>
                    <div className="fw-semibold fs-5">{formatRequestCount(serviceApiMetrics.previous_month_requests)}</div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-muted small">API Requests Fulfilled (Current Month)</div>
                    <div className="fw-semibold fs-5">{formatRequestCount(serviceApiMetrics.current_month_requests)}</div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-muted small">MoM Growth</div>
                    <div className="fw-semibold fs-5">{formatPercent(serviceApiMetrics.mom_growth_pct)}</div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-muted small">API Requests Fulfilled (Previous YTD)</div>
                    <div className="fw-semibold">{formatRequestCount(serviceApiMetrics.previous_ytd_requests)}</div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-muted small">API Requests Fulfilled (Current YTD)</div>
                    <div className="fw-semibold">{formatRequestCount(serviceApiMetrics.current_ytd_requests)}</div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-muted small">YoY Growth</div>
                    <div className="fw-semibold">{formatPercent(serviceApiMetrics.yoy_growth_pct)}</div>
                  </div>
                </div>
              </SectionCard>
              <SectionCard title="API Requests Fulfilled (12 Month History)">
                <BarChart data={serviceApiMetrics.monthly_request_trend || []} />
              </SectionCard>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h5 className="card-title">Current Month Utilization</h5>
              <div className="text-muted small mb-3">Current-month organization and API key utilization, including allocation pressure and plan-level averages.</div>
              <SectionCard title="Top 5 Organizations by API Utilization" first>
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <FourColumnTableColGroup />
                    <thead>
                      <tr>
                        <th>Organization Name</th>
                        <th>Registration Date</th>
                        <th>Subscription</th>
                        <th># Requests</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(serviceApiMetrics.top_orgs_month || []).length === 0 ? (
                        <tr><td colSpan={4} className="text-muted">No usage recorded this month</td></tr>
                      ) : serviceApiMetrics.top_orgs_month.map((item) => (
                        <tr key={`top-month-${item.org_id}`}>
                          <td>{item.org_name}</td>
                          <td>{formatDateCell(item.registration_date)}</td>
                          <td>{formatSubscriptionType(item.subscription_name, item.billing_cadence)}</td>
                          <td>{formatRequestCount(item.request_count)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
              <SectionCard title="Organizations at Monthly Allocation Limit">
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <SixColumnTableColGroup />
                    <thead>
                      <tr>
                        <th>Organization Name</th>
                        <th>Registration Date</th>
                        <th>Subscription</th>
                        <th>Allocation</th>
                        <th># Requests</th>
                        <th>% Utilized</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(serviceApiMetrics.customers_maxed_out || []).length === 0 ? (
                        <tr><td colSpan={6} className="text-muted">No organizations are at their monthly allocation limit</td></tr>
                      ) : serviceApiMetrics.customers_maxed_out.map((item) => (
                        <tr key={`maxed-${item.org_id}`}>
                          <td>{item.org_name}</td>
                          <td>{formatDateCell(item.registration_date)}</td>
                          <td>{formatSubscriptionType(item.subscription_name, item.billing_cadence)}</td>
                          <td>{formatRequestCount(item.api_rate_limit)}</td>
                          <td>{formatRequestCount(item.request_count)}</td>
                          <td>{formatPlainPercent(item.utilization_pct)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
              <SectionCard title="Organizations Nearing Monthly Allocation">
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <SixColumnTableColGroup />
                    <thead>
                      <tr>
                        <th>Organization Name</th>
                        <th>Registration Date</th>
                        <th>Subscription</th>
                        <th>Allocation</th>
                        <th># Requests</th>
                        <th>% Utilized</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(serviceApiMetrics.customers_near_limit || []).length === 0 ? (
                        <tr><td colSpan={6} className="text-muted">No customers are above the near-limit threshold</td></tr>
                      ) : serviceApiMetrics.customers_near_limit.map((item) => (
                        <tr key={`near-${item.org_id}`}>
                          <td>{item.org_name}</td>
                          <td>{formatDateCell(item.registration_date)}</td>
                          <td>{formatSubscriptionType(item.subscription_name, item.billing_cadence)}</td>
                          <td>{formatRequestCount(item.api_rate_limit)}</td>
                          <td>{formatRequestCount(item.request_count)}</td>
                          <td>{formatPlainPercent(item.utilization_pct)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
              <SectionCard title="Average Utilization by Subscription Type">
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <FourColumnUtilTableColGroup />
                    <thead>
                      <tr>
                        <th>Subscription Type</th>
                        <th># Active Orgs</th>
                        <th>Total Requests</th>
                        <th>Average Requests per Org</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(serviceApiMetrics.average_by_subscription_type_month || []).length === 0 ? (
                        <tr><td colSpan={4} className="text-muted">No active utilization recorded this month</td></tr>
                      ) : serviceApiMetrics.average_by_subscription_type_month.map((item) => (
                        <tr key={`avg-${item.subscription_name}-${item.billing_cadence}`}>
                          <td>{formatSubscriptionType(item.subscription_name, item.billing_cadence)}</td>
                          <td>{formatRequestCount(item.active_org_count)}</td>
                          <td>{formatRequestCount(item.total_requests)}</td>
                          <td>{formatRequestCount(item.avg_requests_per_org)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
              <SectionCard title="Top 5 API Keys by Utilization">
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <FourColumnTableColGroup />
                    <thead>
                      <tr>
                        <th>Organization Name</th>
                        <th>API Key Name</th>
                        <th>Key Creation Date</th>
                        <th># Requests</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(serviceApiMetrics.top_api_keys_month || []).length === 0 ? (
                        <tr><td colSpan={4} className="text-muted">No API key utilization recorded this month</td></tr>
                      ) : serviceApiMetrics.top_api_keys_month.map((item) => (
                        <tr key={`key-${item.key_id}`}>
                          <td>{item.org_name}</td>
                          <td>{item.friendly_name || 'Unnamed Key'}</td>
                          <td>{formatDateCell(item.key_created_at)}</td>
                          <td>{formatRequestCount(item.request_count)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h5 className="card-title">YTD Utilization</h5>
              <div className="text-muted small mb-3">Year-to-date organization utilization based on monthly Service API usage totals.</div>
              <SectionCard title="Top 5 Organizations by API Utilization" first>
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <FourColumnTableColGroup />
                    <thead>
                      <tr>
                        <th>Organization Name</th>
                        <th>Registration Date</th>
                        <th>Subscription</th>
                        <th># Requests</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(serviceApiMetrics.top_orgs_year || []).length === 0 ? (
                        <tr><td colSpan={4} className="text-muted">No usage recorded this year</td></tr>
                      ) : serviceApiMetrics.top_orgs_year.map((item) => (
                        <tr key={`top-year-${item.org_id}`}>
                          <td>{item.org_name}</td>
                          <td>{formatDateCell(item.registration_date)}</td>
                          <td>{formatSubscriptionType(item.subscription_name, item.billing_cadence)}</td>
                          <td>{formatRequestCount(item.request_count)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
            </div>
          </div>
        </>
      )}
      {!loading && metrics && activeTab === 'service-api-endpoint-analytics' && (
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title">Service API Endpoint Analytics</h5>
            <div className="text-muted small mb-3">Current-month endpoint analytics for authenticated requests with a resolved organization. Invalid-key traffic is not included here.</div>
            <SectionCard title="Top Endpoints by Request Volume" first>
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <FiveColumnTableColGroup />
                  <thead>
                    <tr>
                      <th>Endpoint</th>
                      <th>Method</th>
                      <th># Requests</th>
                      <th>Avg Response Time</th>
                      <th># Active Orgs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(serviceApiRouteMetrics.top_endpoints_month || []).length === 0 ? (
                      <tr><td colSpan={5} className="text-muted">No route analytics recorded this month</td></tr>
                    ) : serviceApiRouteMetrics.top_endpoints_month.map((item) => (
                      <tr key={`route-top-${item.http_method}-${item.route_pattern}`}>
                        <td className="text-break">{item.route_pattern}</td>
                        <td>{item.http_method}</td>
                        <td>{formatRequestCount(item.request_count)}</td>
                        <td>{formatDurationMs(item.avg_duration_ms)}</td>
                        <td>{formatRequestCount(item.active_org_count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
            <SectionCard title="Slowest Endpoints">
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <FiveColumnTableColGroup />
                  <thead>
                    <tr>
                      <th>Endpoint</th>
                      <th>Method</th>
                      <th>Avg Response Time</th>
                      <th># Requests</th>
                      <th>Max Response Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(serviceApiRouteMetrics.slowest_endpoints_month || []).length === 0 ? (
                      <tr><td colSpan={5} className="text-muted">No endpoint latency data with a sufficient sample size this month</td></tr>
                    ) : serviceApiRouteMetrics.slowest_endpoints_month.map((item) => (
                      <tr key={`route-slowest-${item.http_method}-${item.route_pattern}`}>
                        <td className="text-break">{item.route_pattern}</td>
                        <td>{item.http_method}</td>
                        <td>{formatDurationMs(item.avg_duration_ms)}</td>
                        <td>{formatRequestCount(item.request_count)}</td>
                        <td>{formatDurationMs(item.max_duration_ms)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
            <SectionCard title="Highest Server Error Rate Endpoints">
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <FiveColumnTableColGroup />
                  <thead>
                    <tr>
                      <th>Endpoint</th>
                      <th>Method</th>
                      <th>Server Error Rate</th>
                      <th># Requests</th>
                      <th>5xx Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(serviceApiRouteMetrics.highest_server_error_rate_endpoints_month || []).length === 0 ? (
                      <tr><td colSpan={5} className="text-muted">No server-error endpoint data with a sufficient sample size this month</td></tr>
                    ) : serviceApiRouteMetrics.highest_server_error_rate_endpoints_month.map((item) => (
                      <tr key={`route-server-errors-${item.http_method}-${item.route_pattern}`}>
                        <td className="text-break">{item.route_pattern}</td>
                        <td>{item.http_method}</td>
                        <td>{formatErrorPercent(item.server_error_rate_pct)}</td>
                        <td>{formatRequestCount(item.request_count)}</td>
                        <td>{formatRequestCount(item.server_error_count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
            <SectionCard title="Highest Rejected Request Rate Endpoints">
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <FiveColumnTableColGroup />
                  <thead>
                    <tr>
                      <th>Endpoint</th>
                      <th>Method</th>
                      <th>Rejected Rate</th>
                      <th># Requests</th>
                      <th>Rejection Mix</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(serviceApiRouteMetrics.highest_rejected_request_rate_endpoints_month || []).length === 0 ? (
                      <tr><td colSpan={5} className="text-muted">No rejected-request endpoint data with a sufficient sample size this month</td></tr>
                    ) : serviceApiRouteMetrics.highest_rejected_request_rate_endpoints_month.map((item) => (
                      <tr key={`route-rejected-${item.http_method}-${item.route_pattern}`}>
                        <td className="text-break">{item.route_pattern}</td>
                        <td>{item.http_method}</td>
                        <td>{formatErrorPercent(item.rejected_rate_pct)}</td>
                        <td>{formatRequestCount(item.request_count)}</td>
                        <td className="small text-muted">
                          Client {formatRequestCount(item.client_error_count)} · Auth {formatRequestCount(item.auth_error_count)} · 429 {formatRequestCount(item.rate_limited_count)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpsUserMetrics;
